// src/oura/ask/types.ts
//
// Ask Oura (PREVIEW): the shapes a chat message is built from. Every answer
// is plain data, built from the same reads the existing screens use, so a
// message can be rendered, kept in the store and compared without React.
//
// Ask Oura does three jobs (v1 scope, 21 Oct 2026):
//   1. Guide staff on Oura: how do I, where is, what does this mean.
//   2. Answer questions on a client's data, each fact linked to its screen.
//   3. Prepare key work for a named person to say yes to: the approved
//      answers as page proposals, the right document opened in Oura's own
//      Create a doc, the next step on Delivery.
//
// An answer opens with a short paragraph, a numbered citation after each
// sentence it supports, then (only when useful) numbered steps, a table, a
// list of rows that open the existing screens, or one action card. Nothing
// here writes: an action is a link into an existing screen.

/** Where a fact came from: a link into the existing screen that shows it. */
export interface AskSource {
    /** "Delivery", "Visibility · scan 16 Sep". */
    label: string;
    to: string;
}

/** One sentence of prose and the sources it rests on (shown as [1] [2]). */
export interface AskSentence {
    text: string;
    cites?: AskSource[];
}

/** Status colours: amber waits on the client, blue is ours in progress. */
export type AskTone = "amber" | "blue" | "gray";

export interface AskRow {
    title: string;
    sub?: string;
    meta?: string;
    late?: boolean;
    /** The existing screen this row opens (a Delivery drawer, a doc on the Client tab). */
    to: string;
}

export interface AskTableRow {
    label: string;
    value: string;
    late?: boolean;
    cite?: AskSource;
}

/** A link into the exact screen or drawer an answer is about. */
export interface AskGo {
    /** "Open Overview", "Open the request". */
    label: string;
    to: string;
}

/** A group of questions Ask Oura answers, each one a chip that asks it. */
export interface AskTopicGroup {
    title: string;
    items: string[];
}

/* ---------------------- Use case 1: the morning triage -------------------- */

/** One thing that needs us, on one client, with the button that opens it. */
export interface TriageRow {
    id: string;
    businessId: string;
    client: string;
    /** One plain sentence. */
    text: string;
    /** "Due Thu 17 Sep", "6 working days", "Asked 3 hours ago". */
    age: string;
    late?: boolean;
    go: AskGo;
    /** Sort key within the group: older first. */
    since: number;
}

export type TriageKey = "late" | "us" | "yes" | "quiet";

export interface TriageGroup {
    key: TriageKey;
    title: string;
    /** One quiet line under the title: what the group means. */
    hint: string;
    /** The title's count where it is not the row count (Late on Delivery: "6 deliverables, 1 came back"). */
    count?: string;
    /** The group's own clause in the answer's top line, where the row count alone would mislead. */
    lead?: string;
    rows: TriageRow[];
}

/* ---------------------- Use case 3: the client said yes ------------------- */

/** One approved answer as a page change: what the page says now, and what it would say. */
export interface ProposalPreview {
    question: string;
    before: string;
    after: string;
    /** "Siew Ling, 14 Oct". */
    approved: string;
}

/** What happens after a person's yes. `preview` steps are not wired in this build. */
export interface NextStep {
    title: string;
    text: string;
    state: "done" | "now" | "preview";
    go?: AskGo;
}

export type AskBlock =
    | { kind: "p"; sentences: AskSentence[] }
    | { kind: "table"; rows: AskTableRow[] }
    | { kind: "list"; title: string; tone: AskTone; rows: AskRow[]; more?: string }
    /** A how-to: 2 to 4 numbered steps naming the real buttons, and the link that opens the screen. */
    | { kind: "steps"; steps: string[]; go: AskGo }
    /** A where-is or a what-does-this-mean: the link that opens the screen. */
    | { kind: "go"; go: AskGo }
    /** The questions Ask Oura answers, grouped: the How do I menu and the fallback. */
    | { kind: "topics"; groups: AskTopicGroup[] }
    /** Approved answers that can become page changes: the way into the Page workshop. */
    | { kind: "suggest"; text: string; to: string }
    /** Open Oura's own Create a doc with this template picked. Nothing is made here. */
    | { kind: "start"; docType: string; text: string; to: string }
    /** Use case 1: what needs us, grouped by urgency, each row opening its screen. */
    | { kind: "triage"; groups: TriageGroup[] }
    /** Use case 3: the approved answers, before and after. */
    | { kind: "proposals"; origin: string; items: ProposalPreview[] }
    /** Use case 3: what happens after the yes. */
    | { kind: "next"; steps: NextStep[]; note: string }
    /** Several screens, each with one line on what it shows. */
    | { kind: "links"; links: { go: AskGo; text: string }[] }
    /** "Did you mean…": the closest questions Ask Oura answers, each a button that asks it. */
    | { kind: "choices"; items: string[] }
    /** A read the answer needs failed: the button reads the records again and asks the same question. */
    | { kind: "retry"; label: string }
    /**
     * A chat action the server previewed (ourteam-backend #6801): what it would change, in the
     * server's words, with Do it and Not now. Nothing runs until Do it; every action here is reversible.
     */
    | { kind: "action"; businessId: string; name: string; label: string; says: string; params: Record<string, unknown>; undo: string | null };

export interface AskAnswer {
    blocks: AskBlock[];
    /** Two or three follow-up questions that fit this answer. */
    followUps: string[];
    /** The matching had no answer ("Did you mean…"): Oura's AI step may answer instead (live.ts). */
    unsure?: true;
}

/** A picture sent with a question (behind VITE_ENABLE_OURA_ASK_IMAGES). */
export interface AskImage {
    name: string;
    /** image/png, image/jpeg or image/webp. */
    mime: string;
    /** The bytes, base64, no data: prefix: what the server reads. */
    data: string;
    /** A data: URL of the same bytes, for the thumbnail. */
    url: string;
}

export interface AskMessage {
    id: string;
    from: "oura" | "staff";
    /** Staff: what they typed. Oura: the question this answers (for Ask again). */
    text: string;
    /** Pictures sent with the question. On the Oura slot too, so the answer can read them. */
    images?: AskImage[];
    /** Oura only: null while the records are still being read. */
    answer?: AskAnswer | null;
    /** Oura only, while `answer` is null for a server call: what the chat says it is doing ("Asking Oura's AI about…"). */
    waiting?: string;
}

/** What the conversation so far changes about an answer. */
export interface AskCtx {
    /** The full list of what Ask Oura can do is already in this conversation (it is shown at most once). */
    menuShown: boolean;
}

/** Whose records the chat answers from. */
export type AskScope = { kind: "client"; businessId: string } | { kind: "all" };

export const scopeKey = (s: AskScope): string => (s.kind === "client" ? `client:${s.businessId}` : "all");
